// Pure stat pipeline for the player (no Phaser): inventory stats → temporary
// buffs → challenge modifiers → BERSERK → clamps. The inventory owns
// equipment/mutation/upgrade/synergy stats; everything after that is here.

import type { StatBlock, StatKey } from '../../core/types';

export interface BuffMod {
  stat: StatKey;
  op: 'add' | 'mul';
  value: number;
}

export interface Buff {
  id: string;
  mods: readonly BuffMod[];
  /** Seconds left. */
  remaining: number;
}

/** glass_frame challenge: max HP multiplier. */
export const GLASS_FRAME_HP = 0.6;
/** BERSERK flag: active below this fraction of max HP... */
export const BERSERK_THRESHOLD = 0.4;
/** ...granting this multiplier to damage and fire rate. */
export const BERSERK_BONUS = 1.35;

/** Apply a list of modifiers in place: all additions first, then multiplications (order-independent). */
export function applyMods(s: StatBlock, mods: Iterable<BuffMod>): void {
  const muls: BuffMod[] = [];
  for (const m of mods) {
    if (!(m.stat in s) || !Number.isFinite(m.value)) continue;
    if (m.op === 'add') s[m.stat] += m.value;
    else muls.push(m);
  }
  for (const m of muls) s[m.stat] *= m.value;
}

/** Keep every stat inside a sane range (bad data or stacked tradeoffs must never break movement). */
export function clampStats(s: StatBlock): void {
  const atLeast = (k: StatKey, lo: number) => {
    if (!Number.isFinite(s[k]) || s[k] < lo) s[k] = lo;
  };
  const between = (k: StatKey, lo: number, hi: number) => {
    const v = s[k];
    s[k] = !Number.isFinite(v) ? lo : v < lo ? lo : v > hi ? hi : v;
  };
  s.maxHp = Math.max(1, Math.round(Number.isFinite(s.maxHp) ? s.maxHp : 1));
  s.maxShield = Math.max(0, Math.round(Number.isFinite(s.maxShield) ? s.maxShield : 0));
  atLeast('shieldRegen', 0);
  atLeast('shieldDelay', 0.2);
  between('armor', 0, 0.6);
  atLeast('moveSpeed', 0.2);
  atLeast('jumpPower', 0.3);
  s.airJumps = Math.max(0, Math.round(Number.isFinite(s.airJumps) ? s.airJumps : 0));
  s.airDashes = Math.max(0, Math.round(Number.isFinite(s.airDashes) ? s.airDashes : 0));
  atLeast('dashCooldown', 0.1);
  atLeast('dashPower', 0.2);
  atLeast('damage', 0);
  atLeast('fireRate', 0.1);
  atLeast('heatCapacity', 0.1);
  atLeast('cooling', 0.1);
  between('critChance', 0, 1);
  atLeast('critMult', 1);
  atLeast('signatureCooldown', 0.1);
  atLeast('ultimateCharge', 0);
  s.chainBonus = Math.max(0, Math.round(Number.isFinite(s.chainBonus) ? s.chainBonus : 0));
  atLeast('chainDamage', 0);
  atLeast('burnPower', 0);
  atLeast('explosionRadius', 0.1);
  atLeast('explosionDamage', 0);
  atLeast('knockback', 0);
  between('knockbackResist', 0, 1);
  atLeast('pickupRadius', 0);
  atLeast('scrapGain', 0);
  atLeast('damageTaken', 0.1);
}

export interface StatContext {
  buffs: Iterable<Buff>;
  glassFrame: boolean;
}

/** Inventory stats → buffs → glass_frame → clamps. Returns a new block (input untouched). */
export function finalizeStats(inventoryStats: Readonly<StatBlock>, c: StatContext): StatBlock {
  const s: StatBlock = { ...inventoryStats };
  const mods: BuffMod[] = [];
  for (const b of c.buffs) for (const m of b.mods) mods.push(m);
  applyMods(s, mods);
  if (c.glassFrame) s.maxHp = s.maxHp * GLASS_FRAME_HP;
  clampStats(s);
  return s;
}

/** BERSERK condition: flag owned and HP below the threshold of max HP. */
export function berserkActive(hasFlag: boolean, hp: number, maxHp: number): boolean {
  return hasFlag && hp > 0 && hp < BERSERK_THRESHOLD * maxHp;
}

/** In place: the BERSERK bonus (applied after clamping; max HP is unaffected so the condition is stable). */
export function applyBerserk(s: StatBlock): void {
  s.damage *= BERSERK_BONUS;
  s.fireRate *= BERSERK_BONUS;
}

/** Advance buff timers; returns true if any buff expired (stats need a refresh). */
export function tickBuffs(buffs: Map<string, Buff>, dt: number): boolean {
  let expired = false;
  for (const [id, b] of buffs) {
    b.remaining -= dt;
    if (b.remaining <= 0) {
      buffs.delete(id);
      expired = true;
    }
  }
  return expired;
}
